import jsPDF from 'jspdf';
import 'jspdf-autotable';
import NavLogoHlthTek from '../../../assets/images/logo/NavLogo_new.png';
import { getPrescription } from '../api/pharmacy';
import { normalizePrescriptionStatus } from './pharmacyMappers';
import { prescriptionStatusLabel } from './prescriptionStatus';
import { formatDate } from '../utils';
import { HOSPITAL_INFO } from '../constants';
import { IPD_COLORS } from '../../ipd/utils/ipdColors';
// ---IMPORTS-END---

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

// ─── Palette (derived from IPD_COLORS, same convention as poPdf.js) ─────────
const C = {
  teal:      hexToRgb(IPD_COLORS.primary),
  tealDark:  hexToRgb(IPD_COLORS.primaryDark),
  tealLight: hexToRgb(IPD_COLORS.primaryLight),
  tealLt:    hexToRgb(IPD_COLORS.chipInfoBg),
  cardBg:    hexToRgb(IPD_COLORS.white),
  light:     hexToRgb(IPD_COLORS.pageBg),
  altRow:    hexToRgb(IPD_COLORS.panelHeaderBg),
  border:    hexToRgb(IPD_COLORS.border),
  muted:     hexToRgb(IPD_COLORS.textSecondary),
  dark:      hexToRgb(IPD_COLORS.textPrimary),
  white:     hexToRgb(IPD_COLORS.white),
  errorText:   hexToRgb(IPD_COLORS.error.text),
  errorBg:     hexToRgb(IPD_COLORS.error.bg),
  warningText: hexToRgb(IPD_COLORS.warning.text),
  warningBg:   hexToRgb(IPD_COLORS.warning.bg),
  mutedBg:     [241, 245, 249],
};

// Mirrors PRESCRIPTION_DOMAIN_COLORS in StatusChip. ACKNOWLEDGED / RECEIVED / CANCEL
// were missing entirely, so the three commonest non-initial states printed as the grey
// default — and CANCELLED never matches, because the backend's code is `Cancel`.
const STATUS_BADGE_COLORS = {
  ACTIVE:               hexToRgb(IPD_COLORS.warning.text),
  SENT:                 hexToRgb(IPD_COLORS.warning.text),
  ACKNOWLEDGED:         hexToRgb(IPD_COLORS.info.text),
  ISSUED:               [ 22, 163,  74],
  DISPENSED:            hexToRgb(IPD_COLORS.primaryLight),
  RECEIVED:             hexToRgb(IPD_COLORS.success.text),
  PARTIALLY_ISSUED:     [180, 120,   0],
  PARTIALLY_DISPENSED:  [180, 120,   0],
  CANCEL:               hexToRgb(IPD_COLORS.error.text),
  CANCELLED:            [220,  38,  38],
};
const SEVERITY_STYLES = {
  SEVERE:   { text: C.errorText,   bg: C.errorBg   },
  HIGH:     { text: C.errorText,   bg: C.errorBg   },
  MODERATE: { text: C.warningText, bg: C.warningBg },
  MEDIUM:   { text: C.warningText, bg: C.warningBg },
  MILD:     { text: C.muted,       bg: C.mutedBg   },
  LOW:      { text: C.muted,       bg: C.mutedBg   },
};

function badgeColor(status) { return STATUS_BADGE_COLORS[status] || [100, 116, 139]; }
function severityStyle(sev) { return SEVERITY_STYLES[String(sev || '').toUpperCase()] || { text: C.dark, bg: C.mutedBg }; }

// ─── Layout constants ────────────────────────────────────────────────────────
const SIDE_M        = 12;
const HEADER_BAND_H = 25;   // teal hospital band at very top of every page
const ROW_H         = 6.8;  // patient-grid row height (single-line label:value)
const CHROME_H      = 90;   // header band + patient card + doctor card (repeats every page)
const FOOTER_H      = 18;   // hospital band (repeats every page)
const TABLE_FONT    = 8.3;
const TABLE_HEAD_F  = 8.6;
const RADIUS        = 2.2;

function pick(row, ...keys) {
  if (!row || typeof row !== 'object') return '';
  for (const key of keys) {
    const value = row[key];
    if (value != null && value !== '') return value;
  }
  return '';
}

// ─── Normalize raw prescription row into a flat view for rendering ──────────
function extractPrescriptionView(row) {
  const flag = String(pick(row, 'ipd_flag', 'is_ipd') || '').toLowerCase();
  const ipdOpd = !flag ? '—' : (['yes', 'true', 'ipd', '1'].includes(flag) ? 'IPD' : 'OPD');
  const ageGender = [pick(row, 'age', 'patient_age'), pick(row, 'gender')].filter(Boolean).join(' / ') || '—';

  const medicinesRaw = Array.isArray(row?.medicines) && row.medicines.length
    ? row.medicines
    : (Array.isArray(row?.lines) ? row.lines : []);

  return {
    order_number: pick(row, 'prescription_number', 'order_number', 'uhid') || '—',
    date: pick(row, 'created_at', 'date', 'order_date') || '',
    uhid: pick(row, 'uhid') || '—',
    patient_name: pick(row, 'name', 'patient_name') || '—',
    age_gender: ageGender,
    patient_id: pick(row, 'patient_id') || '—',
    phone_no: pick(row, 'phone_no', 'phone') || '—',
    email: pick(row, 'email') || '—',
    ipd_opd: ipdOpd,
    admission_id: pick(row, 'admission_id') || '—',
    bed_no: pick(row, 'bed_no') || '—',
    room_no: pick(row, 'room_no') || '—',
    address: pick(row, 'address_line', 'address') || '—',
    doctor_name: pick(row, 'doctor_name') || '—',
    nurse_name: pick(row, 'nurse_name') || '—',
    status: normalizePrescriptionStatus(pick(row, 'status')),
    // Same label table the on-screen chip uses — a printed order that says something
    // the screen does not is worse than no badge at all.
    status_label: prescriptionStatusLabel(pick(row, 'status')),
    provisional_diagnosis: pick(row, 'provisional_diagnosis'),
    final_diagnosis: pick(row, 'final_diagnosis'),
    allergies: (Array.isArray(row?.allergies) ? row.allergies : []).map((a) => ({
      allergy: pick(a, 'name', 'allergen', 'description') || '—',
      severity: pick(a, 'severity') || '—',
      reaction: pick(a, 'reaction', 'note') || '—',
    })),
    medicines: medicinesRaw.map((m) => ({
      name: pick(m, 'name', 'medicine', 'medicine_name', 'item_name', 'instructions') || '—',
      dose: pick(m, 'dose', 'dosage', 'quantity_per_dose') || '—',
      frequency: pick(m, 'frequency', 'frequency_code') || '—',
      duration: pick(m, 'duration') || '—',
      route: pick(m, 'route', 'dosage_form', 'administration_route') || '—',
      notes: pick(m, 'notes', 'instructions') || '—',
    })),
    additional_instructions: (Array.isArray(row?.additional_instructions) ? row.additional_instructions : []).filter(Boolean),
  };
}

// ─── Logo ─────────────────────────────────────────────────────────────────────
function addLogo(doc, rightEdgeX, topY, maxW, maxH) {
  try {
    const props = doc.getImageProperties(NavLogoHlthTek);
    const ratio = props.width / props.height;
    let w = maxW, h = w / ratio;
    if (h > maxH) { h = maxH; w = h * ratio; }
    doc.addImage(NavLogoHlthTek, 'PNG', rightEdgeX - w, topY, w, h);
  } catch (_) { /* logo optional */ }
}

// ─── Small drawing primitives ────────────────────────────────────────────────
function drawCard(doc, x, y, w, h, { fill = C.cardBg, border = C.border, radius = RADIUS } = {}) {
  doc.setFillColor(...fill);
  doc.setDrawColor(...border);
  doc.setLineWidth(0.3);
  doc.roundedRect(x, y, w, h, radius, radius, 'FD');
}

// Draws "LABEL: value" inline on a single baseline (label bold+muted, value normal+dark).
function fieldRow(doc, x, y, label, value, maxW) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.8);
  doc.setTextColor(...C.muted);
  const labelText = `${label.toUpperCase()}:`;
  doc.text(labelText, x, y);
  const labelW = doc.getTextWidth(labelText);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.3);
  doc.setTextColor(...C.dark);
  const valueX = x + labelW + 2;
  const valueMaxW = Math.max(maxW - labelW - 2, 10);
  doc.text(doc.splitTextToSize(String(value || '—'), valueMaxW)[0] || '—', valueX, y);
}

function sectionTitle(doc, text, x, y, w) {
  doc.setFillColor(...C.teal);
  doc.rect(x, y - 3.4, 1.3, 4.6, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(...C.dark);
  doc.setCharSpace(0.4);
  doc.text(text.toUpperCase(), x + 4, y);
  doc.setCharSpace(0);
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.25);
  doc.line(x, y + 2.2, x + w, y + 2.2);
}

// ─── Repeating chrome: teal band + patient card + doctor card ───────────────
// Height is fixed (single-line values only) so it matches CHROME_H on every page.
function drawPageChrome(doc, rx, W) {
  const U = W - 2 * SIDE_M;

  doc.setFillColor(...C.teal);
  doc.rect(0, 0, W, HEADER_BAND_H, 'F');
  doc.setFillColor(...C.tealLight);
  doc.rect(0, HEADER_BAND_H - 1.1, W, 1.1, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15.5);
  doc.setTextColor(...C.white);
  doc.text(HOSPITAL_INFO.name, SIDE_M, 11.5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.3);
  doc.setTextColor(210, 224, 240);
  doc.text(`${HOSPITAL_INFO.tagline}  ·  Prescription (View Only Copy)`, SIDE_M, 17.8);

  addLogo(doc, W - SIDE_M, 3.5, 32, 12);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(210, 224, 240);
  doc.text(`Rx: ${rx.order_number}   ·   ${formatDate(rx.date)}`, W - SIDE_M, 21, { align: 'right' });

  let y = HEADER_BAND_H + 6.5;
  sectionTitle(doc, 'Patient Information', SIDE_M, y, U);
  y += 5.5;

  const cardY = y;
  const topPad = 3;
  const gridH = ROW_H * 3;
  const cardH = topPad + gridH + ROW_H + 4;
  drawCard(doc, SIDE_M, cardY, U, cardH, { fill: C.white });

  const colW = U / 3;
  const padX = 4;
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.15);
  doc.line(SIDE_M + colW, cardY + topPad, SIDE_M + colW, cardY + topPad + gridH - 2);
  doc.line(SIDE_M + colW * 2, cardY + topPad, SIDE_M + colW * 2, cardY + topPad + gridH - 2);

  const grid = [
    [['UHID', rx.uhid], ['Patient Name', rx.patient_name], ['Age/Gender', rx.age_gender]],
    [['Patient ID', rx.patient_id], ['Phone', rx.phone_no], ['Email', rx.email]],
    [['IPD/OPD', rx.ipd_opd], ['Admission ID', rx.admission_id], ['Bed No', rx.bed_no]],
  ];
  grid.forEach((row, ri) => {
    const rowY = cardY + topPad + 4.2 + ri * ROW_H;
    row.forEach(([label, val], ci) => {
      fieldRow(doc, SIDE_M + padX + ci * colW, rowY, label, val, colW - padX * 1.5);
    });
  });

  const addrY = cardY + topPad + gridH;
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.2);
  doc.line(SIDE_M + 3, addrY, SIDE_M + U - 3, addrY);
  const addrRowY = addrY + 4.2;
  fieldRow(doc, SIDE_M + padX, addrRowY, 'Room No', rx.room_no, 40);
  fieldRow(doc, SIDE_M + padX + 46, addrRowY, 'Address', rx.address, U - 46 - padX * 2);

  y = cardY + cardH + 5;

  const docCardH = 12.5;
  drawCard(doc, SIDE_M, y, U, docCardH, { fill: C.light });
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.6);
  doc.setTextColor(...C.muted);
  doc.setCharSpace(0.3);
  doc.text('ATTENDING DOCTOR', SIDE_M + padX, y + 4.6);
  doc.setCharSpace(0);
  const nurseX = SIDE_M + U * 0.48;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(...C.dark);
  const doctorMaxW = nurseX - 6 - (SIDE_M + padX) - 3;
  doc.text(doc.splitTextToSize(rx.doctor_name, doctorMaxW)[0] || rx.doctor_name, SIDE_M + padX, y + 9.4);

  const bLabel = (rx.status_label || rx.status || '').replace(/_/g, ' ') || '—';
  doc.setFontSize(7.2);
  const bW = Math.max(doc.getTextWidth(bLabel) + 9, 26);
  const bH = 7;
  const bX = SIDE_M + U - padX - bW;
  const bY = y + (docCardH - bH) / 2;

  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.15);
  doc.line(nurseX - 6, y + 2.5, nurseX - 6, y + docCardH - 2.5);
  fieldRow(doc, nurseX, y + 8, 'Nurse', rx.nurse_name, bX - nurseX - 6);
  doc.setFillColor(...badgeColor(rx.status));
  doc.roundedRect(bX, bY, bW, bH, 1.8, 1.8, 'F');
  doc.setTextColor(...C.white);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text(bLabel, bX + bW / 2, bY + bH / 2 + 1.1, { align: 'center' });
}

// ─── Repeating footer: hospital band (repeats every page) ───────────────────
function drawPageFooter(doc, W, H, pageNum, totalPages) {
  const footerTop = H - FOOTER_H;
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.25);
  doc.line(SIDE_M, footerTop, W - SIDE_M, footerTop);

  const bandH = 11;
  const bandTop = H - bandH;
  doc.setFillColor(...C.teal);
  doc.rect(0, bandTop, W, bandH, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.6);
  doc.setTextColor(...C.white);
  doc.text(HOSPITAL_INFO.name, W / 2, bandTop + 4.6, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.6);
  doc.setTextColor(206, 220, 238);
  const hospLine = [HOSPITAL_INFO.address, HOSPITAL_INFO.phone, HOSPITAL_INFO.email].filter(Boolean).join('   ·   ');
  doc.text(hospLine, W / 2, bandTop + 8.6, { align: 'center' });

  doc.setFontSize(6.2);
  doc.setTextColor(...C.muted);
  doc.text(`Page ${pageNum} / ${totalPages}`, W - SIDE_M, bandTop - 2.2, { align: 'right' });
}

// ─── Core builder ─────────────────────────────────────────────────────────────
export function createPrescriptionPdfDocument({ prescription = {} } = {}) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', compress: true });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const U = W - 2 * SIDE_M;
  const rx = extractPrescriptionView(prescription);

  const newPage = () => {
    doc.addPage();
    drawPageChrome(doc, rx, W);
    return CHROME_H + 6;
  };

  drawPageChrome(doc, rx, W);
  let y = CHROME_H + 6;

  // ── Diagnosis — two symmetric side-by-side cards ──────────────────────────
  const diagGap = 5;
  const diagW = (U - diagGap) / 2;
  const diagH = 23;
  drawCard(doc, SIDE_M, y, diagW, diagH, { fill: C.white });
  drawCard(doc, SIDE_M + diagW + diagGap, y, diagW, diagH, { fill: C.white });

  [
    { x: SIDE_M, label: 'Provisional Diagnosis', value: rx.provisional_diagnosis },
    { x: SIDE_M + diagW + diagGap, label: 'Final Diagnosis', value: rx.final_diagnosis },
  ].forEach(({ x, label, value }) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.8);
    doc.setTextColor(...C.muted);
    doc.setCharSpace(0.3);
    doc.text(label.toUpperCase(), x + 4, y + 5);
    doc.setCharSpace(0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.6);
    doc.setTextColor(...C.dark);
    const lines = doc.splitTextToSize(value || '—', diagW - 8).slice(0, 3);
    doc.text(lines, x + 4, y + 10.5);
  });
  y += diagH + 6;

  // ── Drug Allergies ───────────────────────────────────────────────────────
  sectionTitle(doc, 'Drug Allergies', SIDE_M, y, U);
  y += 5;
  if (rx.allergies.length) {
    doc.autoTable({
      startY: y,
      head: [['Allergy', 'Severity', 'Reaction']],
      body: rx.allergies.map((a) => [a.allergy, a.severity, a.reaction]),
      theme: 'grid',
      styles: { font: 'helvetica', fontSize: TABLE_FONT, cellPadding: { top: 2.6, bottom: 2.6, left: 3, right: 3 }, textColor: C.dark, lineColor: C.border, lineWidth: 0.15, valign: 'middle' },
      headStyles: { fillColor: C.teal, textColor: C.white, fontStyle: 'bold', fontSize: TABLE_HEAD_F, valign: 'middle' },
      alternateRowStyles: { fillColor: C.light },
      columnStyles: {
        0: { cellWidth: U * 0.38, fontStyle: 'bold' },
        1: { cellWidth: U * 0.22, halign: 'center' },
        2: { cellWidth: U * 0.4 },
      },
      tableWidth: U,
      margin: { top: CHROME_H, left: SIDE_M, right: SIDE_M, bottom: FOOTER_H + 4 },
      showHead: 'everyPage',
      didParseCell: ({ column, cell, section }) => {
        if (section === 'body' && column.index === 1) cell.text = [''];
      },
      didDrawCell: ({ column, cell, section, row }) => {
        if (section !== 'body' || column.index !== 1) return;
        const sev = String(rx.allergies[row.index]?.severity || '—');
        const style = severityStyle(sev);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.2);
        const chipW = Math.min(doc.getTextWidth(sev) + 6, cell.width - 4);
        const chipX = cell.x + (cell.width - chipW) / 2;
        const chipY = cell.y + (cell.height - 4.6) / 2;
        doc.setFillColor(...style.bg);
        doc.roundedRect(chipX, chipY, chipW, 4.6, 1.2, 1.2, 'F');
        doc.setTextColor(...style.text);
        doc.text(sev, cell.x + cell.width / 2, chipY + 3.3, { align: 'center' });
      },
      didDrawPage: () => drawPageChrome(doc, rx, W),
    });
    y = doc.lastAutoTable.finalY + 6;
  } else {
    drawCard(doc, SIDE_M, y, U, 9, { fill: C.light });
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(TABLE_FONT);
    doc.setTextColor(...C.muted);
    doc.text('No known drug allergies.', SIDE_M + 4, y + 5.8);
    y += 15;
  }

  // ── Medications ───────────────────────────────────────────────────────────
  sectionTitle(doc, 'Medication Prescribed', SIDE_M, y, U);
  y += 5;
  doc.autoTable({
    startY: y,
    head: [['Sl.No', 'Medicine Name', 'Dose', 'Frequency', 'Duration', 'Route', 'Notes']],
    body: rx.medicines.length
      ? rx.medicines.map((m, i) => [i + 1, m.name, m.dose, m.frequency, m.duration, m.route, m.notes])
      : [['', 'No medicines prescribed.', '', '', '', '', '']],
    theme: 'grid',
    styles: { font: 'helvetica', fontSize: TABLE_FONT, cellPadding: { top: 2.6, bottom: 2.6, left: 3, right: 3 }, textColor: C.dark, lineColor: C.border, lineWidth: 0.15, valign: 'middle' },
    headStyles: { fillColor: C.teal, textColor: C.white, fontStyle: 'bold', fontSize: TABLE_HEAD_F, valign: 'middle' },
    alternateRowStyles: { fillColor: C.light },
    columnStyles: {
      0: { cellWidth: 12, halign: 'center', textColor: C.muted },
      1: { cellWidth: 'auto', fontStyle: 'bold' },
      2: { cellWidth: 18, halign: 'center' },
      3: { cellWidth: 22, halign: 'center' },
      4: { cellWidth: 22, halign: 'center' },
      5: { cellWidth: 18, halign: 'center' },
      6: { cellWidth: 38, fontStyle: 'italic', textColor: C.muted },
    },
    tableWidth: U,
    margin: { top: CHROME_H, left: SIDE_M, right: SIDE_M, bottom: FOOTER_H + 4 },
    showHead: 'everyPage',
    didDrawPage: () => drawPageChrome(doc, rx, W),
  });
  y = doc.lastAutoTable.finalY + 6;

  // ── Additional Info ───────────────────────────────────────────────────────
  if (rx.additional_instructions.length) {
    if (y + 16 > H - FOOTER_H) y = newPage();
    sectionTitle(doc, 'Additional Information', SIDE_M, y, U);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(TABLE_FONT);
    doc.setTextColor(...C.dark);
    rx.additional_instructions.forEach((note) => {
      const lines = doc.splitTextToSize(String(note), U - 9);
      if (y + lines.length * 4.4 > H - FOOTER_H) y = newPage();
      doc.setFillColor(...C.teal);
      doc.circle(SIDE_M + 1.1, y - 1.1, 1, 'F');
      lines.forEach((line, li) => { doc.text(line, SIDE_M + 5, y + li * 4.4); });
      y += lines.length * 4.4 + 2;
    });
  }

  // ── Footer on every page ──────────────────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    drawPageFooter(doc, W, H, p, totalPages);
  }

  return doc;
}

// ─── Public API ───────────────────────────────────────────────────────────────
export function getPrescriptionPdfFilename(prescription) {
  const rx = extractPrescriptionView(prescription || {});
  return `Prescription-${rx.order_number !== '—' ? rx.order_number : (rx.uhid !== '—' ? rx.uhid : 'RX')}.pdf`;
}

export function buildPrescriptionPdfBlob(data) {
  return createPrescriptionPdfDocument(data).output('blob');
}

export function downloadPrescriptionPdfFromData(data) {
  createPrescriptionPdfDocument(data).save(getPrescriptionPdfFilename(data.prescription));
}

export function previewPrescriptionPdfInNewTab(data) {
  const url = createPrescriptionPdfDocument(data).output('bloburl');
  window.open(`${url}#zoom=page-fit`, '_blank');
}

export async function fetchPrescriptionPdfData(prescriptionId) {
  const prescription = await getPrescription(prescriptionId);
  return { prescription };
}
